// src/components/IssueListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import DetailedListView from './DetailedListView';
import { issueQueries, staffQueries, updateQueries } from '../repository/graphQueries'; 
import { formatDate } from '../utils';
import { ISSUE_STATUS_OPTIONS, ISSUE_SEVERITY_OPTIONS, DRI_EXPLANATION } from '../constants';
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="relative inline-block text-left">
            <div>
                <button type="button" className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
            </div>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input type="checkbox" className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" checked={selectedOptions.includes(option)} onChange={() => onChange(option)} />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <div className="relative inline-block">
            <div className="inline-flex items-center cursor-help" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onClick={() => setIsVisible(!isVisible)}>
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full right-0 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full right-2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const cleanDriList = (rawDriList) => {
    if (!rawDriList) return [];
    const separators = / and | & | \/ | \\ | with | for |,| featuring | assisting | cc /i;
    const allNames = rawDriList.flatMap(dri => {
        if (typeof dri !== 'string') return [];
        const cleaned = dri.replace(/\(.*\)|\[.*\]/g, '');
        return cleaned.split(separators);
    });
    const finalDris = allNames
        .map(name => name.trim())
        .filter(name => name && name.length > 1);
    return [...new Set(finalDris)].sort();
};

const issueColumns = [
    { header: 'Name', key: 'name'},
    { header: 'DRI', key: 'assigned_to' },
    { header: 'Severity', key: 'severity' },
    { header: 'Status', key: 'status' },
    { header: 'Blocked Initiatives', key: 'blockedInitiatives', formatter: val => Array.isArray(val) ? val.join(', ') : val },
    { header: 'Connections', key: 'score', align: 'center' }, 
    { header: 'Description', key: 'description', type: 'longText'},
];

const IssueListView = () => {
    const { neo4jService, clientId, dateRange } = useOutletContext();

    const [issuesList, setIssuesList] = useState([]);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [errorIssues, setErrorIssues] = useState(null);

    const [issueSeverityFilter, setIssueSeverityFilter] = useState('');
    const [issueStatusFilter, setIssueStatusFilter] = useState('');

    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(false);
    const [selectedDris, setSelectedDris] = useState([]);
    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoadingDris(true);
        try {
            const driResult = await neo4jService.runQuery(staffQueries.getDistinctDRIsForClient, { clientId });
            const rawDriList = driResult.map(r => r.dri);
            setDris(cleanDriList(rawDriList));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, clientId]);

    const loadIssuesList = useCallback(async () => {
        if (!neo4jService || !clientId || !dateRange || !dateRange.start || !dateRange.end) {
            setLoadingIssues(false);
            return;
        }
        setLoadingIssues(true); 
        setErrorIssues(null);
        
        try {
            const params = {
                clientId,
                severityFilter: issueSeverityFilter || null,
                statusFilter: issueStatusFilter || null,
                startDate: dateRange.start,
                endDate: dateRange.end,
                driFilter: selectedDris.length > 0 ? selectedDris : null
            };
            const issues = await neo4jService.runQuery(issueQueries.getAllIssuesList, params);
            setIssuesList(issues);
        } catch (err) {
            setErrorIssues(err.message); 
            setIssuesList([]);
        } finally {
            setLoadingIssues(false);
        }
    }, [neo4jService, clientId, issueSeverityFilter, issueStatusFilter, dateRange?.start, dateRange?.end, selectedDris]); 

    useEffect(() => {
        if (clientId) {
            loadDris();
        }
    }, [clientId, loadDris]);

    useEffect(() => {
        if (clientId && dateRange && dateRange.start && dateRange.end) {
            loadIssuesList();
        }
    }, [loadIssuesList, clientId, dateRange, selectedDris]); 

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleSeverityFilterChange = (event) => setIssueSeverityFilter(event.target.value);
    const handleStatusFilterChange = (event) => setIssueStatusFilter(event.target.value);

    const handleIssueUpdate = (itemId, updatedField, newValue) => {
        console.log(`Issue ${itemId} field ${updatedField} updated to ${newValue}. Refreshing list.`);
        loadIssuesList(); 
    };

    const issueEditableColumnsConfig = {
        status: {
            options: ISSUE_STATUS_OPTIONS,
            query: updateQueries.updateNodeStatus,
            propertyName: 'status'
        },
        severity: {
            options: ISSUE_SEVERITY_OPTIONS,
            query: updateQueries.updateNodeSeverity,
            propertyName: 'severity'
        }
    };

    return (
        <>
             <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl font-semibold">Issues</h2>
                 <div className="flex items-center space-x-2">
                    <DropdownCheckbox
                        options={dris}
                        selectedOptions={selectedDris}
                        onChange={handleDriChange}
                        title="Filter by DRI"
                    />
                    <Tooltip content={driTooltipContent}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500 cursor-help" />
                    </Tooltip>
                 </div>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="severityFilter" className="block text-sm font-medium text-gray-700 mb-1">Severity:</label>
                    <select id="severityFilter" value={issueSeverityFilter} onChange={handleSeverityFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                         <option value="">All</option>
                         {ISSUE_SEVERITY_OPTIONS.map(sev => (<option key={sev} value={sev}>{sev.charAt(0).toUpperCase() + sev.slice(1)}</option>))}
                    </select>
                </div>
                <div>
                    <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">Status:</label>
                    <select id="statusFilter" value={issueStatusFilter} onChange={handleStatusFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                         <option value="">All (except Resolved)</option>
                         {ISSUE_STATUS_OPTIONS.map(stat => (<option key={stat} value={stat}>{stat.charAt(0).toUpperCase() + stat.slice(1)}</option>))}
                     </select>
                </div>
            </div>

            <DetailedListView
                title="Issues"
                items={issuesList}
                columns={issueColumns}
                isLoading={loadingIssues}
                error={errorIssues}
                entityType="issues"
                neo4jService={neo4jService}
                clientId={clientId}
                onItemUpdate={handleIssueUpdate}
                editableColumnsConfig={issueEditableColumnsConfig}
            />
        </>
    );
};

export default IssueListView;